import type { TimelineFieldChange } from '@/types/timeline'

export interface ParsedStatusUpdate {
  previous: string
  current: string
}

export interface ParsedMerge {
  lostPatientRecordHash: string
}

export interface ParsedLink {
  linkedPatients: string[]
}

export interface ParsedRefund {
  amount: string
}

export interface ParsedSentBy {
  name: string
  source: 'Manual' | 'System'
}

// PHP's REST sanitizer strips parentheses and collapses newlines, so the
// stored note arrives single-line: `Sent by: <name> Subject: ...`. Source is
// inferred from name (literal `System` ⇒ System, anything else ⇒ Manual).
// Also tolerates a trailing `Manual`/`System` token from earlier formats.
const SENT_BY_REGEX = /^Sent by:\s+(.+?)(?:\s+(Manual|System)\b)?(?:\s+(?=Subject:)|\s*\n|\s*$)/

export function parseSentBy(note: string): ParsedSentBy | null {
  const match = note.match(SENT_BY_REGEX)
  if (!match) return null
  const name = match[1].trim()
  const explicit = match[2] as 'Manual' | 'System' | undefined
  return { name, source: explicit ?? (name === 'System' ? 'System' : 'Manual') }
}

export function stripSentByLine(note: string): string {
  const match = note.match(SENT_BY_REGEX)
  return match ? note.slice(match[0].length) : note
}

function formatTimelineFieldValue(value: unknown): string | null {
  if (value == null) return null
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)

  if (Array.isArray(value)) {
    const parts = value
      .map(item => formatTimelineFieldValue(item))
      .filter((item): item is string => item != null && item !== '')

    return parts.length > 0 ? parts.join(', ') : '[]'
  }

  if (typeof value === 'object') {
    const record = value as Record<string, unknown>

    if (typeof record.name === 'string' && record.name.trim() !== '') return record.name
    if (typeof record.label === 'string' && record.label.trim() !== '') return record.label
    if (typeof record.title === 'string' && record.title.trim() !== '') return record.title
    if (typeof record.id === 'string' || typeof record.id === 'number') return String(record.id)

    try {
      return JSON.stringify(value)
    } catch {
      return String(value)
    }
  }

  return String(value)
}

/**
 * Parses `note` for `fields_update` / `update` note_types.
 * Supports two JSON structures:
 * - Patient/Order timeline: `{ fieldName, previous, current }`
 * - Medication request PATCH: `{ field, before, after }`
 */
export function parseFieldsUpdate(note: string): TimelineFieldChange[] | null {
  try {
    const parsed = JSON.parse(note)
    if (!Array.isArray(parsed)) return null
    return parsed.map(
      (item: {
        fieldName?: string
        field?: string
        previous?: unknown
        current?: unknown
        oldValue?: unknown
        newValue?: unknown
        before?: unknown
        after?: unknown
      }) => ({
        field: item.fieldName ?? item.field ?? 'Unknown field',
        oldValue: formatTimelineFieldValue(item.previous ?? item.oldValue ?? item.before),
        newValue: formatTimelineFieldValue(item.current ?? item.newValue ?? item.after) ?? ''
      })
    )
  } catch {
    return null
  }
}

/**
 * Parses `note` for `update_patient_crm` note_types.
 * Expects a JSON object of `{ [fieldName]: { crm, api } }` entries
 * where `api` is the previous value and `crm` is the new value from CRM.
 */
export function parseCrmDifferences(note: string): TimelineFieldChange[] | null {
  try {
    const parsed = JSON.parse(note)
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return null
    const entries = Object.entries(parsed)
    if (entries.length === 0) return null
    return entries.map(([key, value]) => {
      const diff = value as { crm?: string; api?: string }
      return {
        field: key,
        oldValue: diff.api != null ? String(diff.api) : null,
        newValue: diff.crm != null ? String(diff.crm) : ''
      }
    })
  } catch {
    return null
  }
}

/**
 * Parses `note` for `status_update` note_types.
 * Expects a JSON object with `{ previous, current }`.
 */
export function parseStatusUpdate(note: string): ParsedStatusUpdate | null {
  try {
    const parsed = JSON.parse(note)
    if (typeof parsed !== 'object' || parsed === null) return null
    if (typeof parsed.previous !== 'string' || typeof parsed.current !== 'string') return null
    return { previous: parsed.previous, current: parsed.current }
  } catch {
    return null
  }
}

/**
 * Parses `note` for `merge` note_types.
 * Expects a JSON object with `{ lostPatientRecordHash }`.
 */
export function parseMerge(note: string): ParsedMerge | null {
  try {
    const parsed = JSON.parse(note)
    if (typeof parsed !== 'object' || parsed === null) return null
    if (typeof parsed.lostPatientRecordHash !== 'string') return null
    return { lostPatientRecordHash: parsed.lostPatientRecordHash }
  } catch {
    return null
  }
}

/**
 * Parses `note` for `link` note_types.
 * Expects a JSON object with `{ linkedPatients: string[] }`.
 */
export function parseLink(note: string): ParsedLink | null {
  try {
    const parsed = JSON.parse(note)
    if (typeof parsed !== 'object' || parsed === null) return null
    if (!Array.isArray(parsed.linkedPatients)) return null
    return { linkedPatients: parsed.linkedPatients }
  } catch {
    return null
  }
}

/**
 * Parses `note` for `refund` note_types.
 * Expects a JSON object with `{ amount }`.
 */
export function parseRefund(note: string): ParsedRefund | null {
  try {
    const parsed = JSON.parse(note)
    if (typeof parsed !== 'object' || parsed === null) return null
    if (typeof parsed.amount !== 'string') return null // We should always expect the amount to be in the form of a string.
    return { amount: parsed.amount }
  } catch {
    return null
  }
}
